/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import { Link } from 'gatsby'
import parse, { domToReact } from 'html-react-parser'
import styled from 'styled-components'

// Services
import convertStyle from 'services/convertStyle'

// Elements
import ButtonPrimarySmall from 'components/elements/Buttons/ButtonPrimarySmall'
import ButtonPrimaryBig from 'components/elements/Buttons/ButtonPrimaryBig'
import ButtonPrimaryTransparent from 'components/elements/Buttons/ButtonPrimaryTransparent'
import ButtonSecondaryTransparent from 'components/elements/Buttons/ButtonSecondaryTransparent'
import TextGradient from 'components/elements/Text/TextGradient'

const Content = styled.div`
  font-weight: ${(props) => props.theme.font.weight.light};
  font-size: ${({ theme }) => theme.font.size.medium};

  & strong,
  & b {
    font-weight: ${(props) => props.theme.font.weight.bold};
    & span {
      font-weight: ${(props) => props.theme.font.weight.bold};
    }
  }

  & h1 {
    @media (min-width: 1439px) {
      font-size: 75px !important;
    }
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5 {
    text-align: initial;
    font-weight: ${(props) => props.theme.font.weight.light};
    font-family: ${(props) => props.theme.font.family.secondary};

    & strong,
    & b {
      font-weight: ${(props) => props.theme.font.weight.semiBold};
    }

    @media (min-width: 992px) {
      font-size: ${({ theme }) => theme.font.size.mega};
      line-height: ${({ theme }) => theme.font.size.mega};
    }

    @media (max-width: 991px) {
      font-size: ${({ theme }) => theme.font.size.quote} !important;
      line-height: 40px !important;
    }

    @media (max-width: 575px) {
      font-size: 36px !important;
      line-height: 30px !important;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  & ul {
    & li {
    }
  }

  & p {
    margin-bottom: 0;
    line-height: ${({ theme }) => theme.font.size.huge};
    @media (max-width: 991px) {
      font-size: 14px !important;
    }
  }

  & a {
    color: ${({ theme }) => theme.color.secondary};
    text-decoration: underline;
  }

  & img {
  }

  /*
    WP WYSIWYG
  */
  .alignright {
    float: right;
    text-align: right;
  }

  .aligncenter {
    display: block;
    margin: 0 auto;
    text-align: center;
  }

  .wp-caption {
    max-width: 100%;
  }

  .wp-caption-text {
    font-size: ${({ theme }) => theme.font.size.tiny};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    text-align: center;
  }
`

const findAndReplace = (content: string) =>
  parse(content, {
    replace: (domNode) => {
      const { name, attribs, children }: any = domNode

      if (name === 'a' && attribs.class === 'button-primary-small') {
        return (
          <ButtonPrimarySmall to={attribs.href} target={attribs.target || ''}>
            {domToReact(children, {})}
          </ButtonPrimarySmall>
        )
      }

      if (name === 'a' && attribs.class === 'button-primary-big') {
        return (
          <ButtonPrimaryBig to={attribs.href} target={attribs.target || ''}>
            {domToReact(children, {})}
          </ButtonPrimaryBig>
        )
      }

      if (name === 'a' && attribs.class === 'button-primary-transparent') {
        return (
          <ButtonPrimaryTransparent
            to={attribs.href}
            target={attribs.target || ''}
          >
            {domToReact(children, {})}
          </ButtonPrimaryTransparent>
        )
      }

      if (name === 'a' && attribs.class === 'button-secondary-transparent') {
        return (
          <ButtonSecondaryTransparent
            to={attribs.href}
            target={attribs.target || ''}
          >
            {domToReact(children, {})}
          </ButtonSecondaryTransparent>
        )
      }

      if (
        (name === 'h1' && attribs.class === 'text-gradient') ||
        (name === 'h2' && attribs.class === 'text-gradient') ||
        (name === 'h3' && attribs.class === 'text-gradient') ||
        (name === 'h4' && attribs.class === 'text-gradient') ||
        (name === 'h5' && attribs.class === 'text-gradient') ||
        (name === 'h6' && attribs.class === 'text-gradient') ||
        (name === 'p' && attribs.class === 'text-gradient') ||
        (name === 'span' && attribs.class === 'text-gradient')
      ) {
        return <TextGradient>{domToReact(children, {})}</TextGradient>
      }

      if (name === 'a') {
        const attributes = attribs

        if (attributes.style) {
          attributes.style = convertStyle(attributes.style)
        }

        if (
          attributes.href.indexOf('www.') !== -1 ||
          attributes.href.indexOf('http:') !== -1 ||
          attributes.href.indexOf('https:') !== -1 ||
          attributes.href.indexOf('mailto:') !== -1 ||
          attributes.href.indexOf('tel:') !== -1
        ) {
          return (
            <a {...attributes} target="_blank" rel="noreferrer">
              {domToReact(children, {})}
            </a>
          )
        }

        return (
          <Link to={attributes.href} {...attributes}>
            {domToReact(children, {})}
          </Link>
        )
      }

      return null
    },
  })

interface ParseContentProps {
  content?: string
  className?: string
}

const ParseContent: React.FC<ParseContentProps> = ({
  content,
  className = '',
}) => {
  if (!content) {
    return null
  }

  return <Content className={className}>{findAndReplace(content)}</Content>
}

export default ParseContent
